import { Link, type LinkProps } from '@tanstack/react-router';
import type { FC, ForwardRefExoticComponent, SVGProps } from 'react';
import { cn } from '@/shared/lib/utils';
import { Separator } from '@/shared/ui/separator/Separator';
import { Text } from '@/shared/ui/text';
import { Tooltip } from '@/shared/ui/tooltip/Tooltip';

export type NavMenuItem = {
  key: string;
  icon?: ForwardRefExoticComponent<SVGProps<SVGSVGElement>>;
  label: string;
  to: NonNullable<LinkProps['to']>;
  params?: LinkProps['params'];
  search?: LinkProps['search'];
  isActive: boolean;
  isDisabled?: boolean;
  hasBottomSeparator?: boolean;
  sectionHeader?: string;
};

type Props = {
  items: NavMenuItem[];
  /** サイドバーが開いているか（折りたたみ時はアイコンのみ表示） */
  isOpen?: boolean;
};

/**
 * ナビゲーションメニューアイテムのレンダリング
 *
 * 第1階層（SidePanel）と第2階層（SecondaryPanel）で共通利用する。
 * isOpen=false の場合はアイコンのみ表示し、ツールチップでラベルを表示する。
 */
export const NavMenuItems: FC<Props> = ({ items, isOpen = true }) => {
  return (
    <nav className="flex flex-col">
      {items.map((item) => {
        const Icon = item.icon;

        const iconAndLabel = (
          <>
            {Icon && <Icon className="size-4 shrink-0 text-text-muted" />}
            {isOpen && (
              <Text
                as="span"
                truncate
                className={cn(
                  Icon && 'ml-2',
                  item.isActive ? 'text-text-base' : 'text-text-secondary',
                )}
              >
                {item.label}
              </Text>
            )}
          </>
        );

        const baseClassName = cn(
          'flex items-center',
          'h-9',
          'rounded-md',
          isOpen ? 'w-full px-3' : 'w-9 justify-center',
          'overflow-hidden',
        );

        const content = item.isDisabled ? (
          <div className={cn(baseClassName, 'cursor-not-allowed opacity-40')}>{iconAndLabel}</div>
        ) : (
          <Link
            to={item.to}
            params={item.params}
            search={item.search}
            className={cn(
              baseClassName,
              'transition-all duration-200',
              item.isActive ? 'bg-surface-strength2' : 'hover:bg-[rgba(12,10,9,0.04)]',
              'cursor-pointer',
            )}
          >
            {iconAndLabel}
          </Link>
        );

        return (
          <div key={item.key}>
            {item.sectionHeader && isOpen && (
              <Text className="mt-2 mb-2 whitespace-nowrap px-3" variant={'description'}>
                {item.sectionHeader}
              </Text>
            )}
            {isOpen ? (
              content
            ) : (
              <Tooltip side="right" tooltipContent={item.label}>
                {content}
              </Tooltip>
            )}
            {item.hasBottomSeparator && <Separator className="my-1" />}
          </div>
        );
      })}
    </nav>
  );
};
